import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  IconButton,
  Chip,
  Paper,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  FormGroup,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { QuestionDto, AnswerType } from '../../types/questions';

interface QuestionPreviewModalProps {
  open: boolean;
  onClose: () => void;
  questions: QuestionDto[];
  professionName: string;
}

const QuestionPreviewModal: React.FC<QuestionPreviewModalProps> = ({
  open,
  onClose,
  questions,
  professionName,
}) => {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState<{ [key: string]: any }>({});

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleAnswer = (value: any) => {
    setAnswers({
      ...answers,
      [currentQuestion.questionKey]: value,
    });
  };

  const renderAnswerInput = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.answerType) {
      case AnswerType.BOOLEAN:
        return (
          <RadioGroup
            value={answers[currentQuestion.questionKey] || ''}
            onChange={(e) => handleAnswer(e.target.value === 'true')}
          >
            <FormControlLabel value="true" control={<Radio />} label="Yes" />
            <FormControlLabel value="false" control={<Radio />} label="No" />
          </RadioGroup>
        );

      case AnswerType.RADIO:
        return (
          <RadioGroup
            value={answers[currentQuestion.questionKey] || ''}
            onChange={(e) => handleAnswer(e.target.value)}
          >
            {currentQuestion.options?.map((option) => (
              <FormControlLabel
                key={option.key}
                value={option.key}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        );

      case AnswerType.MULTISELECT:
        return (
          <FormGroup>
            {currentQuestion.options?.map((option) => (
              <FormControlLabel
                key={option.key}
                control={
                  <Checkbox
                    checked={answers[currentQuestion.questionKey]?.includes(option.key) || false}
                    onChange={(e) => {
                      const current = answers[currentQuestion.questionKey] || [];
                      if (e.target.checked) {
                        handleAnswer([...current, option.key]);
                      } else {
                        handleAnswer(current.filter((k: string) => k !== option.key));
                      }
                    }}
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>
        );

      case AnswerType.TEXT:
      case AnswerType.TEXT_AREA:
        return (
          <TextField
            fullWidth
            multiline={currentQuestion.answerType === AnswerType.TEXT_AREA}
            rows={currentQuestion.answerType === AnswerType.TEXT_AREA ? 4 : 1}
            value={answers[currentQuestion.questionKey] || ''}
            onChange={(e) => handleAnswer(e.target.value)}
            placeholder="Type your answer here..."
            variant="outlined"
          />
        );

      case AnswerType.NUMBER:
        return (
          <TextField
            fullWidth
            type="number"
            value={answers[currentQuestion.questionKey] || ''}
            onChange={(e) => handleAnswer(e.target.value)}
            placeholder="Enter a number..."
            variant="outlined"
          />
        );

      case AnswerType.DATE:
        return (
          <TextField
            fullWidth
            type="date"
            value={answers[currentQuestion.questionKey] || ''}
            onChange={(e) => handleAnswer(e.target.value)}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
          />
        );

      case AnswerType.FILE:
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Typography color="text.secondary">
              File upload would appear here
            </Typography>
          </Paper>
        );

      case AnswerType.LIST_OBJECT:
        return (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              List Item Fields:
            </Typography>
            <Stack spacing={1}>
              {currentQuestion.listItemSchema?.map((field) => (
                <Chip
                  key={field.fieldName}
                  label={`${field.label} (${field.fieldType})`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Paper>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{professionName} Preview</Typography>
            <Typography variant="caption" color="text.secondary">
              Question {currentIndex + 1} of {questions.length}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Box sx={{ height: 4, bgcolor: 'background.paper' }}>
        <Box
          sx={{
            height: '100%',
            width: `${progress}%`,
            bgcolor: 'primary.main',
            transition: 'width 0.3s ease',
          }}
        />
      </Box>

      <DialogContent>
        {currentQuestion && (
          <Stack spacing={3}>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <Chip
                  label={`Question ${currentQuestion.questionNumber}`}
                  size="small"
                  color="primary"
                />
                {currentQuestion.isRequiredAnswer && (
                  <Chip label="Required" size="small" color="error" variant="outlined" />
                )}
                {answers[currentQuestion.questionKey] !== undefined && (
                  <Chip
                    icon={<CheckIcon />}
                    label="Answered"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="h6" gutterBottom>
                {currentQuestion.question}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Type: {currentQuestion.answerType}
              </Typography>
            </Box>

            <Divider />

            <Box>{renderAnswerInput()}</Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<PrevIcon />}
          onClick={handlePrev}
          disabled={currentIndex === 0}
        >
          Previous
        </Button>
        <Box sx={{ flex: 1 }} />
        {currentIndex === questions.length - 1 ? (
          <Button variant="contained" onClick={onClose}>
            Finish Preview
          </Button>
        ) : (
          <Button
            variant="contained"
            endIcon={<NextIcon />}
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QuestionPreviewModal;